import React, { useState, useEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import {
  Button,
  ButtonToolbar,
  Form,
  Input,
  InputNumber,
  Panel,
  SelectPicker,
  Notification,
  toaster,
  Divider
} from 'rsuite'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import AppLoader from '@components/common/AppLoader'

import { ROUTE_SETTINGS_TOPOLOGY } from '@@partner:config/routes'

import { useFetchTopology, TopologyCity } from '@@partner:graphql/query/topology.gql'
import { useSaveCity, CityInput, TranslationInput, GeoPointInput } from '@@partner:graphql/mutation/topology/saveCity.gql'

interface CityFormPageParams {
  cityId?: string
}

interface FormData {
  id?: string
  name: string
  region_id: string
  default_shop_id?: string
  geo_point?: GeoPointInput
  i18n: TranslationInput[]
}

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  region_id: '',
  default_shop_id: undefined,
  geo_point: undefined,
  i18n: []
}

const LANGUAGES = [
  { code: 'ru', label: 'Русский' },
  { code: 'kk', label: 'Казахский' },
  { code: 'en', label: 'English' }
]

export default function CityFormPage () {
  const history = useHistory()
  const { cityId } = useParams<CityFormPageParams>()
  const isEditing = !!cityId

  const { data: topologyData, loading: topologyLoading } = useFetchTopology()
  const [ saveCity, { loading: saving } ] = useSaveCity()

  const [ formData, setFormData ] = useState<FormData>(DEFAULT_FORM_DATA)

  const regionOptions = useMemo(() => {
    const options: { label: string; value: string }[] = []
    topologyData?.topology?.countries?.forEach(country => {
      country.regions?.forEach(region => {
        options.push({
          label: `${region.name} (${country.name})`,
          value: region.id
        })
      })
    })
    return options
  }, [ topologyData ])

  useEffect(() => {
    if (isEditing && topologyData?.topology?.countries) {
      // Find city in topology data
      for (const country of topologyData.topology.countries) {
        for (const region of country.regions ?? []) {
          const city = region.cities?.find(c => c.id === cityId)
          if (city) {
            const geo = city.defaultGeoPoint
            setFormData({
              id: city.id,
              name: city.name,
              region_id: city.region_id,
              geo_point: geo ? {
                latitude: geo.latitude ?? geo.geo_lat,
                longitude: geo.longitude ?? geo.geo_lon
              } : undefined,
              i18n: city.i18n ?? []
            })
            return
          }
        }
      }
    }
  }, [ isEditing, cityId, topologyData ])

  const handleTranslationChange = (langCode: string, name: string) => {
    const newI18n = [ ...formData.i18n ]
    const existingIndex = newI18n.findIndex(t => t.language_code === langCode)
    
    if (existingIndex >= 0) {
      newI18n[existingIndex] = { language_code: langCode, name }
    }
    else {
      newI18n.push({ language_code: langCode, name })
    }
    
    setFormData({ ...formData, i18n: newI18n })
  }

  const getTranslation = (langCode: string) => {
    return formData.i18n.find(t => t.language_code === langCode)?.name ?? ''
  }

  const handleGeoPointChange = (field: 'latitude' | 'longitude', value: number | null) => {
    const newGeoPoint = { ...formData.geo_point }
    if (value !== null) {
      newGeoPoint[field] = value
    }
    else {
      delete newGeoPoint[field]
    }
    
    // Only set geo_point if at least one coordinate is present
    const hasCoordinates = newGeoPoint.latitude !== undefined || newGeoPoint.longitude !== undefined
    setFormData({ 
      ...formData, 
      geo_point: hasCoordinates ? newGeoPoint : undefined 
    })
  }

  const handleSubmit = async () => {
    if (!formData.name || !formData.region_id) {
      toaster.push(
        <Notification type="error" header="Ошибка">Заполните обязательные поля (Название, Регион)</Notification>,
        { placement: 'topEnd' }
      )
      return
    }

    try {
      const cityInput: CityInput = {
        name: formData.name,
        region_id: formData.region_id,
        i18n: formData.i18n.filter(t => t.name)
      }

      if (isEditing && formData.id) {
        cityInput.id = formData.id
      }

      if (formData.geo_point?.latitude !== undefined && formData.geo_point?.longitude !== undefined) {
        cityInput.geo_point = formData.geo_point
      }

      if (formData.default_shop_id) {
        cityInput.default_shop_id = formData.default_shop_id
      }

      await saveCity({
        variables: {
          cityInput,
          upsert: !isEditing
        }
      })

      toaster.push(
        <Notification type="success" header="Успех">
          Город {isEditing ? 'обновлён' : 'создан'}
        </Notification>,
        { placement: 'topEnd' }
      )

      history.push(ROUTE_SETTINGS_TOPOLOGY)
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }

  if (topologyLoading && isEditing) {
    return <AppLoader />
  }

  return (
    <div className="CityFormPage">
      <h1 className="u-heading">{isEditing ? 'Редактирование города' : 'Создание города'}</h1>
      <SettingsTopMenu />

      <Panel shaded className="mt-4">
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Регион *</Form.ControlLabel>
            <SelectPicker
              data={regionOptions}
              value={formData.region_id}
              onChange={(value) => setFormData({ ...formData, region_id: value ?? '' })}
              placeholder="Выберите регион"
              block
              searchable
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Название (по умолчанию) *</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              placeholder="Алматы"
            />
          </Form.Group>

          <Divider>Координаты</Divider>

          <Form.Group>
            <Form.ControlLabel>Широта (latitude)</Form.ControlLabel>
            <InputNumber
              value={formData.geo_point?.latitude}
              onChange={(value) => handleGeoPointChange('latitude', value as number)}
              placeholder="43.2389"
              step={0.0001}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Долгота (longitude)</Form.ControlLabel>
            <InputNumber
              value={formData.geo_point?.longitude}
              onChange={(value) => handleGeoPointChange('longitude', value as number)}
              placeholder="76.9458"
              step={0.0001}
            />
          </Form.Group>

          <Divider>Переводы</Divider>

          {LANGUAGES.map(lang => (
            <Form.Group key={lang.code}>
              <Form.ControlLabel>Название ({lang.label})</Form.ControlLabel>
              <Input
                value={getTranslation(lang.code)}
                onChange={(value) => handleTranslationChange(lang.code, value)}
                placeholder={`Название на ${lang.label.toLowerCase()}`}
              />
            </Form.Group>
          ))}

          <ButtonToolbar className="mt-6">
            <Button
              appearance="primary"
              onClick={handleSubmit}
              loading={saving}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
            <Button
              appearance="subtle"
              onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY)}
            >
              Отмена
            </Button>
          </ButtonToolbar>
        </Form>
      </Panel>
    </div>
  )
}
